import dayjs from 'dayjs';
import type { DateInputValue } from '../../components/foundations/forms/dateInput';
import type { TimeInputValue } from '../../components/foundations/forms/timeInput';

import {
  esES,
  deDE,
  enUS,
  type PickersInputLocaleText,
  nlNL,
  zhCN,
  zhTW,
  hrHR,
  fiFI,
  frFR,
  elGR,
  itIT,
  jaJP,
  koKR,
  plPL,
  ptPT,
  roRO,
  ruRU,
  svSE,
  urPK,
  viVN,
} from '@mui/x-date-pickers/locales';

import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(isSameOrAfter);
dayjs.extend(relativeTime);

export const isPastDate = (dateValue: DateInputValue): boolean => {
  const dt = dayjs(dateValue);
  return dt.isValid() && !dt.isSameOrAfter(new Date(), 'day');
};

export const isPastTime = (
  date: DateInputValue,
  time: TimeInputValue,
): boolean => {
  // If we don't have a date or time, then it's impossible for the time to be in the past.
  if (date === null || time === null) {
    return false;
  }

  if (!isValidDate(time)) {
    return false;
  }

  const timeDate = new Date(time);
  const fullDate = new Date(date);
  // Set the date to use the time passed in.
  fullDate.setHours(
    timeDate.getHours(),
    timeDate.getMinutes(),
    timeDate.getSeconds(),
    timeDate.getMilliseconds(),
  );

  // Ensure the date/time is past the current time.
  return !dayjs(fullDate).isAfter(new Date());
};

export const isToday = (date: DateInputValue): boolean =>
  dayjs(date).isSame(Date.now(), 'day');

export const isValidDate = (value: Date): boolean => {
  // It doesn't matter what the format is as long we take into account date + time.
  const date = dayjs(value);
  return date.isValid() && date.toDate() instanceof Date;
};

export const getPickersInputLocaleText = (
  locale: string,
): PickersInputLocaleText<dayjs.Dayjs> => {
  switch (locale) {
    // german
    case 'de':
      return deDE.components.MuiLocalizationProvider.defaultProps.localeText;

    // spanish
    case 'es':
      return esES.components.MuiLocalizationProvider.defaultProps.localeText;

    // dutch
    case 'nl':
      return nlNL.components.MuiLocalizationProvider.defaultProps.localeText;

    // chinese simplified
    case 'zh-CN':
      return zhCN.components.MuiLocalizationProvider.defaultProps.localeText;

    // chinese traditional
    case 'zh-TW':
      return zhTW.components.MuiLocalizationProvider.defaultProps.localeText;

    // croatian
    case 'hr':
      return hrHR.components.MuiLocalizationProvider.defaultProps.localeText;

    // finnish
    case 'fi':
      return fiFI.components.MuiLocalizationProvider.defaultProps.localeText;

    // french
    case 'fr':
      return frFR.components.MuiLocalizationProvider.defaultProps.localeText;

    // greek
    case 'el':
      return elGR.components.MuiLocalizationProvider.defaultProps.localeText;

    // italian
    case 'it':
      return itIT.components.MuiLocalizationProvider.defaultProps.localeText;

    // japanese
    case 'ja':
      return jaJP.components.MuiLocalizationProvider.defaultProps.localeText;

    // korean
    case 'ko':
      return koKR.components.MuiLocalizationProvider.defaultProps.localeText;

    // polish
    case 'pl':
      return plPL.components.MuiLocalizationProvider.defaultProps.localeText;

    // portuguese
    case 'pt':
      return ptPT.components.MuiLocalizationProvider.defaultProps.localeText;

    // romanian
    case 'ro':
      return roRO.components.MuiLocalizationProvider.defaultProps.localeText;

    // russian
    case 'ru':
      return ruRU.components.MuiLocalizationProvider.defaultProps.localeText;

    // swedish
    case 'sv':
      return svSE.components.MuiLocalizationProvider.defaultProps.localeText;

    // urdu
    case 'ur':
      return urPK.components.MuiLocalizationProvider.defaultProps.localeText;

    // vietnamese
    case 'vi':
      return viVN.components.MuiLocalizationProvider.defaultProps.localeText;

    // english
    default:
      return enUS.components.MuiLocalizationProvider.defaultProps.localeText;
  }
};

/**
 * Formats a date using locale-specific regional preferences from browser settings.
 * Fixes where preferred locale (eg. 'en') defaults to US formats, by using region locale if available
 * @param locale - Base locale code from @doxyme/locale (eg. 'en', 'fr', 'zh-cn')
 * @returns locale string matching preferred locale with any relevant region (eg. 'en-gb', 'fr-ca')
 */
export const getRegionalLocale = (locale: string): string => {
  const baseLanguage = locale.split('-')[0] ?? locale;

  const localeWithRegion = navigator.language.startsWith(`${baseLanguage}-`)
    ? navigator.language.toLowerCase()
    : locale;

  return localeWithRegion;
};

/**
 * Format a date/timestamp as a relative time string (e.g. "2 hours ago", "3 days ago")
 */
export const formatRelativeTime = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return '—';
  return dayjs(date).fromNow();
};
